import CryptoJS from "crypto-js";
import { getStore } from "../redux/storeInjector";
import ApiCall from "./ApiCall";
import { LANGUAGES } from "./Constants";

const MODE = process.env.REACT_APP_MODE || "development";
const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8000/";
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || "my-secret-key@123";
const PUBLIC_URL = process.env.PUBLIC_URL || "/";

export const fetchLanguages = async () => {
  try {
    const response = await ApiCall("GET", "languages");
    if (response?.data?.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
  } catch (error) {
    // Silently catch and fallback to local constant
  }
  return LANGUAGES;
};

const helper = {
  ApiUrl: BASE_URL + "api/v1/",
  validExtensions: ["jpg", "png", "jpeg", "svg"],
  baseUrl: BASE_URL,
  ImgUrl: BASE_URL + "app/uploads/images/",
  ImgResizeUrl: BASE_URL + "app/uploads/images/resize/",
  fetchLanguages,
};

export const getAuthToken = () => {
  // First check localStorage directly in case store isn't hydrated yet
  const tokenFromStorage = localStorage.getItem("authToken");

  // Then check Redux store
  const store = getStore();
  if (!store) return tokenFromStorage || null;
  const state = store.getState();

  const tokenFromStore = state.auth.token;

  // Return whichever is available, preferring the store
  return tokenFromStorage || tokenFromStore || null;
};

export const getCurrentUser = () => {
  const store = getStore();
  if (!store) return null;
  const state = store.getState();
  const userFromStore = state.auth?.user;
  if (userFromStore) return userFromStore;

  const userFromStorage = localStorage.getItem("user");
  if (userFromStorage) {
    try {
      return JSON.parse(userFromStorage);
    } catch (e) {
      return null;
    }
  }
  return null;
};

export function ApiHeaders(token) {
  let apiHeader = {};
  if (token != null) {
    apiHeader["Authorization"] = `Bearer ${token}`;
  }
  apiHeader["Access-Control-Allow-Origin"] = "*";
  apiHeader["Accept"] = "application/json";

  return apiHeader;
}

export function encryptData(val) {
  return CryptoJS.AES.encrypt(val, SECRET_KEY).toString();
}

export function decryptData(val) {
  const bytes = CryptoJS.AES.decrypt(val, SECRET_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
}

export const parseCoordinates = (coordString) => {
  if (!coordString) return { lat: 0, lng: 0 };

  const match = coordString.match(/\(([^,]+),\s*([^)]+)\)/);
  if (!match) return { lat: 0, lng: 0 };

  return {
    lat: parseFloat(match[1]),
    lng: parseFloat(match[2]),
  };
};

export const formatCoordinates = (lat, lng) => {
  return `(${lat},${lng})`;
};

export const fetchAvatarOptions = async () => {
  const token = getAuthToken();
  const currentUser = getCurrentUser();

  let endpoint = "avatar";
  if (token && currentUser?.id) {
    endpoint = `avatar?user_id=${currentUser.id}`;
  }

  const response = await ApiCall("get", endpoint, null, token);
  if (response.data.success) {
    return response.data.data.images;
  } else {
    throw new Error(response.data.message || "Failed to fetch avatars");
  }
};

export const fetchWords = async (timeframe = "day", filters = {}) => {
  const token = getAuthToken();

  try {
    let url = `fetch-words?timeframe=${timeframe}`;
    if (filters.world) url += `&world=${filters.world}`;
    if (filters.countries && filters.countries.length > 0)
      url += `&countries=${filters.countries.join(",")}`;
    if (filters.cities && filters.cities.length > 0)
      url += `&cities=${filters.cities.join(",")}`;

    const response = await ApiCall("GET", url, null, token);
    const data = response?.data?.data.data;
    if (data) {
      return {
        world: data.world || { tags: [], percentages: [] },
        country: data.country || { tags: [], percentages: [] },
        city: data.city || { tags: [], percentages: [] },
        countryLabel: data.countryLabel || "",
        cityLabel: data.cityLabel || "",
      };
    }
  } catch (error) {
    console.error("Failed to fetch dream counts:", error);
  }
};

export const fetchCounts = async () => {
  const token = getAuthToken();
  const response = await ApiCall("GET", "fetch-counts", null, token);
  if (response?.data?.success) {
    return response.data.data.counts;
  } else {
    throw new Error(response?.data?.message || "Failed to fetch counts");
  }
};

export const fetchAdminCounts = async () => {
  const token = getAuthToken();
  const response = await ApiCall("GET", "fetch-admin-counts", null, token);
  if (response?.data?.success) {
    return response.data.data.counts;
  } else {
    throw new Error(response?.data?.message || "Failed to fetch admin counts");
  }
};

export const saveComparisonState = (state) => {
  const user = getCurrentUser();
  const userId = user?.id;
  const key = userId
    ? `dreamComparisonState_${userId}`
    : "dreamComparisonState";

  if (
    !state ||
    (state.comparingDream === undefined &&
      state.chainDream === undefined &&
      state.isDeepComparing === undefined &&
      state.isDeepChaining === undefined &&
      !state.currentDreamId &&
      !state.currentDreamTitle)
  ) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(
      key,
      JSON.stringify({
        comparingDream:
          state.comparingDream !== undefined ? state.comparingDream : false,
        chainDream: state.chainDream !== undefined ? state.chainDream : false,
        isDeepComparing:
          state.isDeepComparing !== undefined ? state.isDeepComparing : false,
        isDeepChaining:
          state.isDeepChaining !== undefined ? state.isDeepChaining : false,
        currentDreamId: state.currentDreamId || null,
        currentDreamTitle: state.currentDreamTitle || null,
      }),
    );
  }
};

export const loadComparisonState = () => {
  const user = getCurrentUser();
  const userId = user?.id;
  const key = userId
    ? `dreamComparisonState_${userId}`
    : "dreamComparisonState";
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : null;
};

export const formatTimeAgo = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1)
    return interval === 1 ? "1 year ago" : `${interval} years ago`;

  interval = Math.floor(seconds / 2592000);
  if (interval >= 1)
    return interval === 1 ? "1 month ago" : `${interval} months ago`;

  interval = Math.floor(seconds / 86400);
  if (interval >= 1)
    return interval === 1 ? "1 day ago" : `${interval} days ago`;

  interval = Math.floor(seconds / 3600);
  if (interval >= 1)
    return interval === 1 ? "1 hour ago" : `${interval} hours ago`;

  interval = Math.floor(seconds / 60);
  if (interval >= 1)
    return interval === 1 ? "1 min ago" : `${interval} mins ago`;

  return "Just now";
};

export const formatTooltipDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const datePart = date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // Format time part (24-hour)
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${datePart} at ${timePart}`;
};

export const formatDreamDate = (dateString) => {
  if (!dateString) return "";

  // Expected format: "MM/DD/YYYY HH:MM" or similar with space-separated date and time
  const parts = dateString.split(" ");
  if (parts.length < 2) return dateString;

  const datePart = parts[0];
  const timePart = parts[1];

  const timeParts = timePart.split(":");
  if (timeParts.length < 2) return dateString;

  let hour = parseInt(timeParts[0], 10);
  const minute = timeParts[1];

  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12; // the hour '0' should be '12'

  const formattedHour = hour < 10 ? `0${hour}` : hour;

  return `${datePart} ${formattedHour}:${minute} ${ampm}`;
};

export const formatJustDate = (dateString) => {
  if (!dateString) return "";
  const parts = dateString.split(" ");
  return parts[0];
};

export const formatAdminDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}-${dd}-${yyyy}`;
};

export const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "");

  // Format as (XXX) XXX-XXXX
  let formatted = digits;
  if (digits.length > 3 && digits.length <= 6) {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else if (digits.length > 6) {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
      10,
    )}`;
  }
  return formatted;
};

/**
 * Support permissions an admin keeps while inside another account.
 *
 * The list is authored server-side (ImpersonationContext::SUPPORTED_PERMISSIONS);
 * `user.impersonation.granted_permissions` is the server's answer for THIS
 * session, so the UI never recomputes elevation and cannot drift from what the
 * API will actually allow. Editing localStorage cannot forge it — the elevation
 * rides on a signed token ability.
 */
export const getImpersonationContext = (user) => {
  const context = (user || getCurrentUser())?.impersonation;
  return context?.is_impersonating ? context : null;
};

/**
 * True when a permission is being granted by the admin who entered this
 * account, rather than held by the account itself. Used to label admin-only
 * controls so support staff aren't misled about what the user can see.
 */
export const isAdminGrantedPermission = (permission, user) => {
  const resolved = user || getCurrentUser();
  if (!resolved) return false;
  if (resolved.permissions?.includes(permission)) return false;
  return !!getImpersonationContext(resolved)?.granted_permissions?.includes(
    permission,
  );
};

export const hasPermission = (permission) => {
  const user = getCurrentUser();
  if (!user) return false;
  if (isUserAdmin(user)) return true;
  // An impersonating admin keeps their own access to the support features the
  // server granted, so entering an account no longer strips those tools.
  if (isAdminGrantedPermission(permission, user)) return true;
  if (!user.permissions) return false;
  return user.permissions.includes(permission);
};

export const encodeId = (id) => {
  if (!id && id !== 0) return "";
  return btoa(String(id)).replace(/=+$/, ""); // Remove padding for cleaner URLs
};

export const decodeId = (encodedId) => {
  if (!encodedId) return "";
  try {
    return atob(encodedId);
  } catch (e) {
    console.error("Decoding error:", e);
    return "";
  }
};

export const isAdmin = (user) => {
  const store = getStore();
  const globalAdminVisibility = store?.getState()?.auth?.adminVisibility;

  // If global admin visibility is OFF (i.e. Global Incognito is ON/enabled), hide stroke/badge
  if (globalAdminVisibility === false) {
    return false;
  }

  // Otherwise, fall back to individual preference (1 = visible/show stroke, 0 = hidden/hide stroke)
  return (
    user?.admin_incognito === 1 ||
    user?.admin_incognito === true ||
    user?.admin_incognito === "1"
  );
};

export const isUserAdmin = (user) => {
  const userRoles =
    user?.roles?.map((r) => (typeof r === "string" ? r : r.name)) || [];
  return userRoles.some(
    (role) => role === "Admin" || role === "SuperAdmin" || role === "admin",
  );
};

export const getUserTitle = (user) => {
  if (!user) return null;
  const dreamCount = user.total_dreams || 0;
  if (dreamCount >= 10000) return "Dreamwalker";
  if (dreamCount >= 1000) return "Dreamweaver";
  if (dreamCount >= 500) return "Dream Machine";
  if (dreamCount >= 100) return "Dreamer";
  return null;
};

export const getDreamCountBadgeText = (dreamCount) => {
  if (dreamCount >= 10000) return "10k";
  if (dreamCount >= 1000) return "1k";
  if (dreamCount >= 500) return "500";
  if (dreamCount >= 100) return "100";
  return null;
};

/**
 * Returns true when the current device is a mobile / touch device.
 * Uses both the user-agent string and the pointer media feature so it
 * works reliably on modern browsers and unusual UAs alike.
 */
export const isMobileDevice = () =>
  /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  ) || window.matchMedia("(pointer: coarse)").matches;

export const customSlowScroll = (
  target,
  duration = 1200,
  alignment = "top",
  offset = 0,
) => {
  if (typeof window === "undefined" || !target) return;

  let container = window;
  let isWindow = true;

  const getScrollContainer = (el) => {
    let parent = el.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY =
        style.getPropertyValue("overflow-y") ||
        style.getPropertyValue("overflow");
      if (
        (overflowY.includes("auto") || overflowY.includes("scroll")) &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return window;
  };

  if (target instanceof HTMLElement) {
    container = getScrollContainer(target);
    isWindow = container === window;
  }

  let startPosition = 0;
  let targetPosition = 0;

  if (isWindow) {
    startPosition =
      window.pageYOffset ||
      window.scrollY ||
      document.documentElement.scrollTop;
    if (target instanceof HTMLElement) {
      const rect = target.getBoundingClientRect();
      const elementTop = rect.top + startPosition;
      if (alignment === "center") {
        targetPosition =
          elementTop - window.innerHeight / 2 + target.clientHeight / 2;
      } else {
        targetPosition = elementTop - offset;
      }
    } else if (typeof target === "number") {
      targetPosition = target;
    }
  } else {
    startPosition = container.scrollTop;
    if (target instanceof HTMLElement) {
      const rect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const elementTop = rect.top - containerRect.top + startPosition;
      if (alignment === "center") {
        targetPosition =
          elementTop - container.clientHeight / 2 + target.clientHeight / 2;
      } else {
        targetPosition = elementTop - offset;
      }
    } else if (typeof target === "number") {
      targetPosition = target;
    }
  }

  const maxScroll = isWindow
    ? document.documentElement.scrollHeight - window.innerHeight
    : container.scrollHeight - container.clientHeight;
  targetPosition = Math.max(0, Math.min(targetPosition, maxScroll));

  const distance = targetPosition - startPosition;
  if (Math.abs(distance) < 2) return;

  let startTime = null;

  const easeInOutQuad = (t, b, c, d) => {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  const animation = (currentTime) => {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);

    if (isWindow) {
      window.scrollTo(0, run);
    } else {
      container.scrollTop = run;
    }

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    } else {
      if (isWindow) {
        window.scrollTo(0, targetPosition);
      } else {
        container.scrollTop = targetPosition;
      }
    }
  };

  requestAnimationFrame(animation);
};

export default helper;
